import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  Dimensions,
  Animated,
  Modal,
  TouchableWithoutFeedback,
} from 'react-native';
import { useTheme } from '~contexts/ThemeContext';
import { OrderDetail } from '~types/api/event';
import { useSetAttendanceStatus } from '~hooks/order/useSetAttendanceStatus';
import { useGetAttendanceStatus } from '~hooks/order/useGetAttendanceStatus';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useTranslation } from 'react-i18next';
import { useGetOrderFinalAddress } from '~hooks/event/useGetOrderFinalAddress';
import { BlurView } from '@react-native-community/blur';

// JSON text content that will be displayed
const eventInfoTexts = {
  reminders: {
    title: "The event starts soon! 🤩",
    text: "Come ready to meet some lovely new people 🤗. Have a wonderful evening ✨"
  },
  location: {
    title: "Location",
    text: "Make sure you know how to get to the venue."
  },
  attendance: {
    question: "Will you attend this event?",
    buttons: {
      onTime: "On Time",
      late: "I'll Be Late",
      notComing: "Not Coming"
    }
  }
};

interface UpcomingEventCardProps {
  event: OrderDetail;
  onDismiss: () => void;
}

const UpcomingEventCard: React.FC<UpcomingEventCardProps> = ({ event, onDismiss }) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [selectedStatus, setSelectedStatus] = useState<'on_time' | 'late' | 'not_coming' | null>(null);
  const { data: addressData, isLoading: addressLoading } = useGetOrderFinalAddress(event.order_id);
  const { data: attendanceStatusData, isLoading: attendanceStatusLoading } = useGetAttendanceStatus(event.order_id);
  
  // Initialize the selected status based on attendance status data
  useEffect(() => {
    if (attendanceStatusData) {
      if (attendanceStatusData.is_on_time) {
        setSelectedStatus('on_time');
      } else if (attendanceStatusData.is_late) {
        setSelectedStatus('late');
      } else if (attendanceStatusData.is_not_coming) {
        setSelectedStatus('not_coming');
      }
    }
  }, [attendanceStatusData]);
  
  // Animation for address card when event is within 24 hours
  const addressPulseAnim = useRef(new Animated.Value(1)).current;
  
  // Modal animation values
  const modalOpacity = useRef(new Animated.Value(0)).current;
  const modalScale = useRef(new Animated.Value(0.8)).current;
  
  // Attendance status hooks
  const { mutate: setAttendanceStatus, isLoading: attendanceLoading } = useSetAttendanceStatus();

  const isLoading = attendanceLoading || addressLoading || attendanceStatusLoading;

  // Calculate hours until event starts and ends
  const eventStartDate = new Date(event.event.start_date);
  const eventEndDate = new Date(event.event.end_date);
  const now = new Date();
  
  // Calculate hours until event in milliseconds and convert to hours
  const hoursUntilEvent = Math.max(0, (eventStartDate.getTime() - now.getTime()) / (1000 * 60 * 60));
  const hoursUntilEventEnd = (eventEndDate.getTime() - now.getTime()) / (1000 * 60 * 60);
  
  // Modal entrance animation
  useEffect(() => {
    // Animate modal entrance
    Animated.parallel([
      Animated.timing(modalOpacity, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.spring(modalScale, {
        toValue: 1,
        friction: 8,
        tension: 40,
        useNativeDriver: true,
      })
    ]).start();
  }, []);

  // Handle modal dismiss with animation
  const handleModalDismiss = () => {
    Animated.parallel([
      Animated.timing(modalOpacity, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(modalScale, {
        toValue: 0.8,
        duration: 200,
        useNativeDriver: true,
      })
    ]).start(() => {
      onDismiss();
    });
  };

  // Animate the address card when event is within 24 hours
  useEffect(() => {
    if (hoursUntilEvent <= 24 && hoursUntilEventEnd > 0 && addressData?.final_address) {
      // Create a pulsing animation to draw attention to the location
      const pulseAnimation = Animated.sequence([
        Animated.timing(addressPulseAnim, {
          toValue: 1.03,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(addressPulseAnim, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
      ]);

      Animated.loop(pulseAnimation, { iterations: 3 }).start();
    }
  }, [addressData, hoursUntilEvent, hoursUntilEventEnd]);
  
  // Format date: Get day name (short), month name (short), and day number
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const dayName = dayNames[eventStartDate.getDay()];
  const monthName = monthNames[eventStartDate.getMonth()];
  const dayNumber = eventStartDate.getDate();
  
  const formattedDate = `${dayName}, ${monthName} ${dayNumber}`;
  
  // Format time: Get hours (12-hour format), minutes, and AM/PM
  let hours = eventStartDate.getHours();
  const minutes = eventStartDate.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  
  // Convert hours to 12-hour format
  hours = hours % 12;
  hours = hours ? hours : 12; // Convert 0 to 12 for 12 AM
  
  const formattedTime = `${hours}:${minutes} ${ampm}`;

  // Handle status selection
  const handleStatusSelection = (status: 'on_time' | 'late' | 'not_coming') => {
    if (isLoading) return;
    
    // Update the selected status
    setSelectedStatus(status);
    
    // Call unified API with the new status
    setAttendanceStatus({ 
      order_id: event.order_id, 
      status_type: status 
    });
  };

  const getButtonStyle = (status: 'on_time' | 'late' | 'not_coming') => ({
    ...styles.button,
    backgroundColor: selectedStatus === status ? colors.primary : 'transparent',
    borderColor: selectedStatus === status ? colors.primary : colors.gray400,
    opacity: (isLoading || selectedStatus === status) ? 0.5 : 1,
  });

  const getTextStyle = (status: 'on_time' | 'late' | 'not_coming') => ({
    ...styles.buttonText,
    color: selectedStatus === status ? colors.white : colors.gray600,
  });

  return (
    <View style={[styles.bottomContainer, { backgroundColor: colors.white }]}>
      {/* Close button at top right */}
      <TouchableOpacity 
        onPress={onDismiss} 
        style={styles.closeButton}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      >
        <Icon name="close" size={24} color={colors.gray500} />
      </TouchableOpacity>
      
      {/* Upcoming Event Label */}
      <View style={[styles.upcomingLabel, { backgroundColor: colors.primary + '15' }]}>
        <Icon name="calendar-clock" size={18} color={colors.primary} style={styles.upcomingIcon} />
        <Text style={[styles.upcomingText, { color: colors.primary }]}>Your upcoming event</Text>
      </View>
      
      <View style={styles.contentContainer}>
        {/* Event Image */}
        {event.event.image_url ? (
          <Image 
            source={{ uri: event.event.image_url }} 
            style={styles.eventImage} 
            resizeMode="cover"
          />
        ) : (
          <View style={[styles.eventImage, { backgroundColor: colors.gray200 }]}>
            <Icon name="calendar-blank" size={32} color={colors.gray500} />
          </View>
        )}
        
        {/* Event Details */}
        <View style={styles.eventDetails}>
          <Text style={[styles.eventName, { color: colors.black }]} numberOfLines={1}>
            {event.event.name}
          </Text>
          <Text style={[styles.dateTime, { color: colors.gray600 }]}>
            {formattedDate} • {formattedTime}
          </Text>
        </View>
      </View>
      
      {/* Location Section - Dynamically rendered based on timing */}
      {hoursUntilEvent <= 24 && hoursUntilEventEnd > 0 && addressData?.final_address ? (
        <Animated.View style={[styles.addressSection, { transform: [{ scale: addressPulseAnim }] }]}>
          <View style={styles.addressHeader}>
            <MaterialIcons name="location-on" size={22} color={colors.primary} style={styles.addressIcon} />
            <Text style={[styles.addressTitle, { color: colors.black }]}>
              {eventInfoTexts.location.title}
            </Text>
          </View>
          <Text style={[styles.addressText, { color: colors.gray600 }]}>
            {addressData.final_address}
          </Text>
          <View style={styles.addressAlert}>
            <MaterialIcons name="access-time" size={18} color={colors.warning || '#F59E0B'} style={styles.alertIcon} />
            <Text style={[styles.addressAlertText, { color: colors.gray600 }]}>
              {hoursUntilEvent > 0
                ? `Starting in ${Math.floor(hoursUntilEvent)} hour${Math.floor(hoursUntilEvent) !== 1 ? 's' : ''}`
                : 'Event has started'}
            </Text>
          </View>
        </Animated.View>
      ) : hoursUntilEventEnd < 0 ? (
        <View style={styles.addressSection}>
          <View style={styles.addressHeader}>
            <MaterialIcons name="location-on" size={22} color={colors.gray500} style={styles.addressIcon} />
            <Text style={[styles.addressTitle, { color: colors.black }]}>
              {eventInfoTexts.location.title}
            </Text>
          </View>
          <Text style={[styles.addressText, { color: colors.gray600 }]}>
            Event was at {addressData?.final_address || 'Location unavailable'}
          </Text>
        </View>
      ) : (
        <View style={styles.addressSection}>
          <View style={styles.addressHeader}>
            <MaterialIcons name="location-on" size={22} color={colors.gray500} style={styles.addressIcon} />
            <Text style={[styles.addressTitle, { color: colors.black }]}>
              {eventInfoTexts.location.title}
            </Text>
          </View>
          <Text style={[styles.addressText, { color: colors.gray600 }]}>
            The exact location will be available 24 hours before the event starts.
          </Text>
        </View>
      )}
      
      {/* Reminder Section */}
      <View style={[styles.reminderSection, { backgroundColor: colors.primary + '10' }]}>
        <Icon name="bell" size={22} color={colors.primary} style={styles.reminderIcon} />
        <View style={styles.reminderTextContainer}>
          <Text style={[styles.reminderTitle, { color: colors.black }]}>
            {eventInfoTexts.reminders.title}
          </Text>
          <Text style={[styles.reminderText, { color: colors.gray600 }]}>
            {eventInfoTexts.reminders.text}
          </Text>
        </View>
      </View>
      
      {/* Attendance Question */}
      <Text style={[styles.attendanceQuestion, { color: colors.black }]}>
        {eventInfoTexts.attendance.question}
      </Text>
      
      {/* Attendance Buttons */}
      <View style={styles.buttonsContainer}>
        {isLoading ? (
          <View style={styles.loaderContainer}>
          <ActivityIndicator size="small" color={colors.primary} style={{alignSelf: 'center'}} />
            </View>
        ) : (
          <>
            <TouchableOpacity
              style={getButtonStyle('on_time')}
              onPress={() => handleStatusSelection('on_time')}
              disabled={isLoading || selectedStatus === 'on_time'}
            >
              <Text style={getTextStyle('on_time')} numberOfLines={1} adjustsFontSizeToFit>
                {eventInfoTexts.attendance.buttons.onTime}
              </Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={getButtonStyle('late')}
              onPress={() => handleStatusSelection('late')}
              disabled={isLoading || selectedStatus === 'late'}
            >
              <Text style={getTextStyle('late')} numberOfLines={1} adjustsFontSizeToFit>
                {eventInfoTexts.attendance.buttons.late}
              </Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={getButtonStyle('not_coming')}
              onPress={() => handleStatusSelection('not_coming')}
              disabled={isLoading || selectedStatus === 'not_coming'}
            >
              <Text style={getTextStyle('not_coming')} numberOfLines={1} adjustsFontSizeToFit>
                {eventInfoTexts.attendance.buttons.notComing}
              </Text>
            </TouchableOpacity>
          </>
        )}
      </View>
    </View>
  );
};

const windowWidth = Dimensions.get('window').width;

const styles = StyleSheet.create({
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 100,
  },
  bottomContainer: {
    width: '100%', // Use percentage for width
    maxWidth: 400, 
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 16, // Reduced from 20 to give more space
    paddingTop: 20, // Increased from 16 to accommodate the upcoming label
    paddingBottom: 24, // Increased to extend background below buttons
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 10,
    maxHeight: '95%', // Limit height to ensure it's scrollable if needed
  },
  closeButton: {
    position: 'absolute',
    top: 12,
    right: 16,
    padding: 4,
    zIndex: 10,
  },
  contentContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    marginTop: 4, // Reduced from 8 to balance with the upcoming label
  },
  eventImage: {
    width: 60,
    height: 60,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  eventDetails: {
    flex: 1,
    marginLeft: 12,
  },
  eventName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  dateTime: {
    fontSize: 14,
  },
  // Address section styles
  addressSection: {
    marginBottom: 16,
    padding: 12,
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: 8,
  },
  addressHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  addressIcon: {
    marginRight: 8,
  },
  addressTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  addressText: {
    fontSize: 14,
    marginLeft: 30,
    marginBottom: 4,
    lineHeight: 20,
  },
  addressTimeInfo: {
    fontSize: 12,
    marginLeft: 30,
    fontStyle: 'italic',
  },
  addressAlert: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    marginLeft: 30,
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 4,
    alignSelf: 'flex-start',
  },
  alertIcon: {
    marginRight: 6,
  },
  addressAlertText: {
    fontSize: 12,
    fontWeight: '500',
  },
  // Reminder section
  reminderSection: {
    flexDirection: 'row',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
    alignItems: 'flex-start',
  },
  reminderIcon: {
    marginRight: 10,
    marginTop: 2,
  },
  reminderTextContainer: {
    flex: 1,
  },
  reminderTitle: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 4,
  },
  reminderText: {
    fontSize: 13,
    lineHeight: 18,
  },
  // Attendance section
  attendanceQuestion: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 10, // Reduced from 12
    textAlign: 'center',
  },
  loaderContainer: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10, // Increased to provide more space within the card background
    paddingHorizontal: 2, // Added padding to ensure buttons fit
  },
  button: {
    flex: 1, 
    padding: 10, // Reduced from 12
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    borderWidth: 1,
    marginHorizontal: 4, // Increased slightly for better spacing
    marginBottom: 4, // Added to ensure buttons don't touch the bottom edge
  },
  buttonText: {
    fontSize: 13, // Reduced from 14
    fontWeight: '500',
    textAlign: 'center', // Ensure text is centered
  },
  // Upcoming label styles
  upcomingLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    borderRadius: 8,
    marginBottom: 12,
    alignSelf: 'flex-start', // Only take up as much width as needed
  },
  upcomingIcon: {
    marginRight: 6,
  },
  upcomingText: {
    fontSize: 14,
    fontWeight: '600',
  },
});

export default UpcomingEventCard;